/**
 * AuthLayoutV2 — redesigned shell for authenticated routes.
 *
 * Mirrors the prototype's CMS chrome:
 *   • Left sidebar (244px) with brand swirl header, grouped nav, soft teal
 *     "active" treatment with a left accent bar
 *   • Top crumb bar + global search slot is left to individual pages for now
 *     (existing v1 page chrome stays inside <main>)
 *   • Bottom of sidebar: user info + my-account + logout
 *
 * Same permission gating and route filtering as v1 (RouteConfig + SessionHelper).
 * Mobile drawer is intentionally trimmed for the first pass — desktop is where
 * 95% of CMS admin time happens; mobile falls back to a slide-in copy of the
 * desktop sidebar.
 */
import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import RouteConfig from '../constants/RouteConfig';
import SessionHelper from '../components/Helper/SessionHelper';
import {
  BrandIcon,
  TopBar,
  CommandPalette,
  cn,
} from '../components/redesign';
import NAV_ICON_BY_PATH from '../components/redesign/navIconMap';
import useScrollToTopOnNavigate from '../hooks/useScrollToTopOnNavigate';

/** Render the brand icon for a given route. Falls back to a soft dot.
 *  Sized to fill the `.nav-item .ico` 20×20 wrapper used by the prototype. */
function NavIcon({ path }) {
  const name = NAV_ICON_BY_PATH[path];
  if (!name) {
    return <span className="w-1.5 h-1.5 rounded-full bg-text-light shrink-0" />;
  }
  return <BrandIcon name={name} size={18} />;
}

// Canonical group order (prototype `cms-nav.js` line 55+). Dashboard sits
// inside the "Workspace" group as its sole item, matching the prototype.
// Routes without a `group` (order < 50, label set) are auto-bucketed into
// "Workspace". Search (/searches) is intentionally dropped — the topbar
// search input replaces it.
const MENU_GROUPS = [
  { key: 'Workspace',                label: 'Workspace' },
  { key: 'Content',                  label: 'Content' },
  { key: 'Captions',                 label: 'Captions' },
  { key: 'Content Assets',           label: 'Content Assets' },
  { key: 'Users',                    label: 'Users' },
  { key: 'Reports',                  label: 'Reports' },
  { key: 'Experiments',              label: 'Experiments' },
  { key: 'Engagement & Promotions',  label: 'Engagement & Promotions' },
  { key: 'Settings & Admin',         label: 'Settings & Admin' },
  { key: 'Integrations',             label: 'Integrations' },
];

const COLLAPSED_STORAGE_KEY = 'cms.nav.collapsed';
// All groups closed by default on first load. The active group force-opens
// (see `forceOpen` in the render), so users always see where they are.
const DEFAULT_COLLAPSED = new Set(MENU_GROUPS.map((g) => g.key));

/** localStorage-backed collapsed-state hook for sidebar groups. */
function useCollapsedGroups() {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const raw = localStorage.getItem(COLLAPSED_STORAGE_KEY);
      if (raw) return new Set(JSON.parse(raw));
    } catch (_) {}
    return new Set(DEFAULT_COLLAPSED);
  });
  const toggle = (key) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      try {
        localStorage.setItem(COLLAPSED_STORAGE_KEY, JSON.stringify([...next]));
      } catch (_) {}
      return next;
    });
  };
  return [collapsed, toggle];
}

/** Chevron used by `.nav-label-chev` (prototype 4938–4946). Rotates -90° when
 *  the parent group is collapsed via a CSS transform on the wrapper. */
const Chevron = (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

function SidebarContents() {
  const location = useLocation();
  const [collapsedSet, toggleCollapsed] = useCollapsedGroups();

  const navRoutes = RouteConfig
    .filter((r) => r.label && SessionHelper.hasPermission(r.requiredPermissions))
    .sort((a, b) => a.order - b.order);

  const isActive = (path) =>
    location.pathname === path ||
    (path !== '/dashboard' && location.pathname.startsWith(path));

  // Bucket every nav-eligible route into a group. Anything without an explicit
  // `group` AND order < 50 (Dashboard, etc.) goes into Workspace. Order >= 50
  // means My Account / Logout — those live in the TopBar's user popover and
  // shouldn't render in the sidebar.
  const groups = MENU_GROUPS.map((g) => ({
    ...g,
    items: navRoutes.filter((r) => {
      if (g.key === 'Workspace') return !r.group && r.order < 50;
      return r.group === g.key;
    }),
  })).filter((g) => g.items.length);

  return (
    <aside
      className={cn(
        'h-full flex flex-col bg-surface border-r border-sand',
        'font-sans text-text',
      )}
      style={{ width: 244, minWidth: 244 }}
    >
      {/* `.sidebar` (prototype 222–229): padding 14 10, gap 4, surface bg,
       *  sand border-right. We use overflow-y-auto so long nav lists scroll. */}
      <nav className="flex-1 overflow-y-auto" style={{ padding: '14px 10px' }}>
        {groups.map((group, idx) => {
          // The active group force-opens — the user always sees their current
          // page in context, even if they explicitly collapsed that group.
          const hasActive = group.items.some((it) => isActive(it.path));
          const isCollapsed = !hasActive && collapsedSet.has(group.key);
          return (
            <div
              key={group.key}
              className={idx === 0 ? '' : 'mt-4'}
            >
              {/* `.nav-label` button variant (prototype 4921–4936):
               *  full width, transparent, Asap Condensed 700 11px uppercase
               *  letter-spacing 0.1em text-muted, padding 8 12 6, sm radius,
               *  transition color+background 140ms ease. */}
              <button
                type="button"
                aria-expanded={!isCollapsed}
                onClick={() => toggleCollapsed(group.key)}
                className={cn(
                  'w-full flex items-center justify-between text-left',
                  'bg-transparent border-0 cursor-pointer rounded-sm',
                  'font-condensed font-bold uppercase text-text-muted',
                  'hover:text-navy hover:bg-cream',
                )}
                style={{
                  gap: 8,
                  padding: '8px 12px 6px',
                  fontSize: 11,
                  letterSpacing: '0.1em',
                  transition: 'color 140ms ease, background 140ms ease',
                }}
              >
                <span>{group.label}</span>
                {/* `.nav-label-chev` (4938–4947) — 16×16 wrapper, 10×10 svg,
                 *  text-muted 0.7 opacity, rotates -90° when group is
                 *  collapsed, 240ms cubic-bezier easing. */}
                <span
                  className="inline-grid place-items-center text-text-muted shrink-0"
                  style={{
                    width: 16,
                    height: 16,
                    opacity: 0.7,
                    transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                    transition: 'transform 240ms cubic-bezier(0.22, 1, 0.36, 1)',
                  }}
                >
                  {Chevron}
                </span>
              </button>

              {/* `.nav-group-items` (4948–4956): overflow hidden, max-height
               *  transitions between 0 and 1200px, opacity 0/1, pointer
               *  events off when collapsed. */}
              <div
                style={{
                  overflow: 'hidden',
                  maxHeight: isCollapsed ? 0 : 1200,
                  opacity: isCollapsed ? 0 : 1,
                  pointerEvents: isCollapsed ? 'none' : 'auto',
                  transition: 'max-height 280ms ease, opacity 200ms ease',
                }}
              >
                {group.items.map((r) => (
                  <NavItem
                    key={r.path}
                    to={r.path}
                    label={r.label}
                    active={isActive(r.path)}
                    icon={<NavIcon path={r.path} />}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </nav>
    </aside>
  );
}

/** `.nav-item` (prototype line 240–262):
 *  • grid 18px | 1fr | auto (we keep flex with explicit gap — visually equiv.)
 *  • padding 8 12, gap 10, 13.5px text, rounded-md
 *  • icon wrapper `.ico` is 20×20 at opacity 0.85
 *  • active state: teal-green-soft bg + teal-green-dark text + 600 weight
 *    + a 3×18 teal accent bar at left:-10px with sm radius on the right side. */
function NavItem({ to, label, active, icon }) {
  return (
    <Link
      to={to}
      className={cn(
        'relative flex items-center no-underline rounded-md',
        active
          ? 'bg-teal-green-soft text-teal-green-dark font-semibold'
          : 'text-text hover:bg-cream hover:text-navy',
      )}
      style={{
        gap: 10,
        padding: '8px 12px',
        fontSize: 13.5,
        margin: '2px 0',
        transition: 'all 160ms ease',
      }}
    >
      {/* `.nav-item.active::before` — 3×18 accent bar, sm radius on the
       *  right side only (left:-10 reaches into the sidebar padding). */}
      {active && (
        <span
          aria-hidden
          className="absolute top-1/2 -translate-y-1/2 bg-teal-green rounded-r-sm"
          style={{ left: -10, width: 3, height: 18 }}
        />
      )}
      {/* `.nav-item .ico` — 20×20 wrapper, opacity 0.85. */}
      {icon && (
        <span
          className="shrink-0 grid place-items-center"
          style={{ width: 20, height: 20, opacity: 0.85 }}
        >
          {icon}
        </span>
      )}
      <span className="truncate flex-1">{label}</span>
    </Link>
  );
}

export default function AuthLayoutV2({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const mainRef = useRef(null);

  // Close mobile drawer on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  // Open every page at the top (the router keeps the previous page's scroll offset otherwise).
  useScrollToTopOnNavigate(mainRef);

  return (
    <div className="min-h-screen flex flex-col bg-bg">
      {/* Global ⌘K palette — self-managed open state + global keydown
       *  listener. Mounted once at the layout level so any screen can
       *  use it (including via the TopBar search trigger). */}
      <CommandPalette />

      {/* Full-width TopBar */}
      <TopBar onOpenMobileNav={() => setMobileOpen(true)} />

      {/* Below: sidebar + main */}
      <div className="flex-1 flex min-h-0">
        {/* Desktop sidebar — hidden on mobile (md breakpoint) */}
        <div className="hidden md:flex shrink-0">
          <SidebarContents />
        </div>

        {/* Mobile drawer — slides in below the topbar */}
        {mobileOpen && (
          <>
            <div
              className="md:hidden fixed inset-x-0 bottom-0 top-14 bg-navy-deep/40 z-40 animate-[fadeIn_180ms_ease-out]"
              onClick={() => setMobileOpen(false)}
              aria-hidden
            />
            <div className="md:hidden fixed top-14 bottom-0 left-0 z-50 animate-[fadeSlideIn_220ms_ease-out]">
              <SidebarContents />
            </div>
          </>
        )}

        <main ref={mainRef} className="flex-1 min-w-0 overflow-auto">{children}</main>
      </div>
    </div>
  );
}
